import { BrowserModule } from '@angular/platform-browser';
import { NgModule, APP_INITIALIZER } from '@angular/core';
import { HttpClientModule, HttpClient } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { registerLocaleData } from '@angular/common';
import localeFrCa from '@angular/common/locales/fr-CA';
import localeEnCa from '@angular/common/locales/en-CA';
import { StoreModule, Store } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { EffectsModule } from '@ngrx/effects';
import { ToastrModule } from 'ngx-toastr';
import { OverlayModule } from '@angular/cdk/overlay';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { OidcSecurityService, StsConfigLoader } from 'angular-auth-oidc-client';
import { DEFAULT_LANG } from 'src/config/lang.config';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { reducers, metaReducers } from './store';
import { environment } from '../environments/environment';
import { GlobalModule } from './global/global.module';
import { UserEffects } from './user/store/user.effects';
import { GlobalEffects } from './global/store/global.effects';
import { AuthEffects } from './auth/store/auth.effects';
import { UserModule } from './user/user.module';
import { AUTH_INTERCEPTOR_PROVIDER } from './auth/interceptor/auth.interceptor';
import { DIAGNOSTICS_INTERCEPTOR_PROVIDER } from './global/services/diagnostics/diagnostics.interceptor';
import { DIAGNOSTICS_ERROR_HANDLER_PROVIDER } from './global/services/diagnostics/diagnostics-error-handler';
import { RequestV2Module } from './request-v2/request-v2.module';
import { ResidenceModule } from './residence/residence.module';
import { MeterModule } from './meter/meter.module';
import { PermitModule } from './permit/permit.module';
import { AuthModule } from './auth/auth.module';
import { oidcAppInitializerFactory } from './auth/initializers/oidc-app.initializer';

registerLocaleData(localeFrCa);
registerLocaleData(localeEnCa);

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
    return new TranslateHttpLoader(http, './assets/i18n/', `.json?cb=${new Date().getTime()}`);
}

@NgModule({
    declarations: [AppComponent],
    imports: [
        TranslateModule.forRoot({
            defaultLanguage: DEFAULT_LANG,
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient],
            },
        }),
        ToastrModule.forRoot({
            positionClass: 'toast-bottom-right',
        }),
        HttpClientModule,
        BrowserModule,
        BrowserAnimationsModule,
        AppRoutingModule,
        AuthModule,
        GlobalModule,
        UserModule,
        RequestV2Module,
        ResidenceModule,
        MeterModule,
        PermitModule,
        OverlayModule,
        StoreModule.forRoot(reducers, { metaReducers }),
        ...(!environment.production ? [StoreDevtoolsModule.instrument({ maxAge: 25 })] : []),
        EffectsModule.forRoot([
            AuthEffects, // has to be first
            GlobalEffects,
            UserEffects,
        ]),
    ],
    providers: [
        // Diagnostics first: responses unwind in reverse, so it only sees errors left after the token refresh.
        DIAGNOSTICS_INTERCEPTOR_PROVIDER,
        AUTH_INTERCEPTOR_PROVIDER,
        DIAGNOSTICS_ERROR_HANDLER_PROVIDER,
        { provide: 'Window', useValue: window },
        {
            provide: APP_INITIALIZER,
            useFactory: oidcAppInitializerFactory,
            deps: [OidcSecurityService, StsConfigLoader, Store],
            multi: true,
        },
    ],
    bootstrap: [AppComponent],
})
export class AppModule {}
